import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { I18nService } from '@app/core';
import { Observable, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService, NotyService } from '@app/services';
import { FormSteps } from '@app/models';
import { HttpClient } from '@angular/common/http';

export enum ViewType {
  Laptop = 'laptop',
  Mobile = 'mobile'
}

declare const toastui: any;

@Component({
  selector: 'app-markdown-handler',
  templateUrl: './markdown-handler.component.html',
  styleUrls: ['./markdown-handler.component.scss'],
  standalone: false
})
export class MarkdownHandlerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() markdownUrl: string; // URL do arquivo markdown
  @Input() formStep: FormSteps;
  @Output() documentMarkdown = new EventEmitter<string>();

  isLoading = false;
  language: string;

  editorInstance: any;
  editorViewerInstance: any;
  currentView: ViewType = ViewType.Mobile;

  @ViewChild('editorContainer', { static: false }) editorContainer!: ElementRef;
  @ViewChild('editorViewer', { static: false }) editorViewer!: ElementRef;

  readonly ViewType = ViewType;
  readonly FormSteps = FormSteps;

  private spaceCounter = 0;

  constructor(
    private i18nService: I18nService,
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private httpClient: HttpClient
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    this.language = (this.i18nService.language || 'en-US').replace('es-MX', 'es-ES');
    this.isLoading = true;
    this.loadEditor()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          if (this.markdownUrl) {
            this.loadMarkdownFile(this.markdownUrl);
          }
        },
        error: error => {
          this.errorHandlerService.handle(error);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.markdownUrl && changes.markdownUrl.currentValue) {
      this.loadMarkdownFile(changes.markdownUrl.currentValue);
    }
    if (changes.formStep && changes.formStep?.currentValue && changes.formStep?.currentValue !== changes?.formStep?.previousValue) {
      if (changes.formStep?.currentValue === FormSteps.extraConfigurations) {
        this.editorInstance.setMarkdown(this.cleanMarkdown(this.editorInstance.getMarkdown()), false);
      }
    }
  }

  ngOnDestroy() {
    if (this.editorInstance) {
      this.editorInstance.destroy();
    }
  }

  toggleViewMode(view: ViewType) {
    if (this.currentView === view) {
      return;
    } else {
      this.currentView = view;
    }
  }

  private loadEditor(): Observable<void> {
    return new Observable<void>(subscriber => {
      const scriptId = 'toastui-script';
      const langScriptId = 'toastui-lang-' + this.language;

      const loadScript = (id: string, src: string): Observable<void> => {
        return new Observable<void>(scriptSubscriber => {
          if (!document.getElementById(id)) {
            const scriptElement = document.createElement('script');
            scriptElement.src = src;
            scriptElement.type = 'text/javascript';
            scriptElement.id = id;
            scriptElement.async = true;

            scriptElement.onload = () => {
              scriptSubscriber.next();
              scriptSubscriber.complete();
            };

            scriptElement.onerror = () => {
              scriptSubscriber.error();
            };

            document.getElementsByTagName('head')[0].appendChild(scriptElement);
          } else {
            scriptSubscriber.next();
            scriptSubscriber.complete();
          }
        });
      };

      // First load ToastUI Editor script, then load language script
      loadScript(scriptId, '/vendors/toastui-editor/toastui-editor-all.min.js')
        .pipe(
          switchMap(() => {
            // en-US is default language for ToastUI Editor, no need to load extra script
            if (this.language === 'en-US') {
              return of(null);
            }
            return loadScript(langScriptId, `/vendors/toastui-editor/i18n/${this.language.toLowerCase()}.js`);
          })
        )
        .subscribe({
          next: () => {
            if (typeof toastui !== 'undefined') {
              toastui.Editor.setLanguage(this.language, {});

              const toolbarItems = [[{ el: this.createHeadingDropdown() }, 'bold', 'italic', 'strike', 'image', 'ol', 'ul', 'table']];

              if (!this.markdownUrl) {
                this.editorInstance = new toastui.Editor({
                  el: this.editorContainer.nativeElement,
                  height: '500px',
                  initialEditType: 'wysiwyg',
                  previewStyle: 'vertical',
                  language: this.language,
                  hideModeSwitch: true,
                  toolbarItems,
                  events: {
                    change: () => {
                      const markdownContent = this.editorInstance.getMarkdown();
                      this.documentMarkdown.emit(markdownContent);
                      this.updateHeadingDropdownState();
                    }
                  },
                  hooks: {
                    addImageBlobHook: (blob: Blob, callback: any) => {
                      this.processImageBlob(blob, url => {
                        const fileName = (blob as File).name || 'image';
                        callback(url, fileName);
                      });
                    }
                  }
                });

                this.editorContainer.nativeElement.addEventListener('paste', this.handlePaste.bind(this), true);
                this.editorContainer.nativeElement.addEventListener('keydown', this.handleKeydown.bind(this), true);
                this.editorContainer.nativeElement.addEventListener('drop', this.handleDrop.bind(this), true);
                this.editorContainer.nativeElement.addEventListener(
                  'mousedown',
                  (event: MouseEvent) => {
                    if (this.formStep === FormSteps.extraConfigurations) this.eventPrevent(event);
                  },
                  true
                );
                this.editorInstance.focus();
              }

              if (this.markdownUrl) {
                this.editorViewerInstance = new toastui.Editor.factory({
                  el: this.editorViewer.nativeElement,
                  height: '100%',
                  viewer: true,
                  language: this.i18nService.language
                });
              }

              this.isLoading = false;
              subscriber.next();
              subscriber.complete();
            } else {
              subscriber.error();
            }
          },
          error: error => {
            this.errorHandlerService.handle(error);
            subscriber.error(error);
          }
        });
    });
  }

  /**
   * Carrega um arquivo Markdown de uma URL e insere no viewer
   */
  private loadMarkdownFile(url: string) {
    this.httpClient.get(url, { responseType: 'text' }).subscribe({
      next: markdownContent => {
        if (this.editorViewerInstance) {
          this.editorViewerInstance.setMarkdown(markdownContent);
        }
      }
    });
  }

  private createHeadingDropdown(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'toastui-editor-toolbar-item-wrapper custom-heading-dropdown';

    const button = document.createElement('button');
    button.className = 'toastui-editor-toolbar-icons dropdown-button';
    button.innerHTML = this.translateService.instant('documents.mainText'); // Estado inicial sempre é <p>
    button.style.cursor = 'pointer';

    const dropdown = document.createElement('ul');
    dropdown.className = 'toastui-editor-popup toastui-editor-popup-add-heading';
    dropdown.style.display = 'none';

    const options = [
      { label: `<p>${this.translateService.instant('documents.mainText')}</p>`, markdown: '', level: 'Paragraph' },
      { label: `<h1>${this.translateService.instant('documents.heading', { headingLevel: 1 })}</h1>`, markdown: '#', level: 1 },
      { label: `<h2>${this.translateService.instant('documents.heading', { headingLevel: 2 })}</h2>`, markdown: '##', level: 2 }
    ];

    options.forEach(option => {
      const item = document.createElement('li');
      item.className = 'toastui-editor-toolbar-icons dropdown-item';
      item.innerHTML = option.label;
      item.style.cursor = 'pointer';
      item.style.padding = '5px';

      item.addEventListener('click', () => {
        button.innerText = option.level === 'Paragraph' ? this.translateService.instant('documents.mainText') : this.translateService.instant('documents.heading', { headingLevel: option.level });
        dropdown.style.display = 'none';

        this.editorInstance.exec('heading', { level: option.level });
      });

      dropdown.appendChild(item);
    });

    button.addEventListener('click', event => {
      event.stopPropagation();
      dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
    });

    document.addEventListener('click', () => {
      dropdown.style.display = 'none';
    });

    wrapper.appendChild(button);
    wrapper.appendChild(dropdown);
    return wrapper;
  }

  private updateHeadingDropdownState() {
    const button = document.querySelector('.toastui-editor-toolbar-icons.dropdown-button');
    if (!this.editorInstance || !button) return;

    const markdown = this.editorInstance.getMarkdown();
    const [cursorPos] = this.editorInstance.getSelection();
    const lines = markdown.split('\n');

    let charCount = 0;
    let lineIndex = 0;

    for (; lineIndex < lines.length; lineIndex++) {
      charCount += lines[lineIndex].length + 1; // +1 for \n
      if (charCount >= cursorPos) break;
    }

    const currentLine = lines[lineIndex] || '';
    let headingLevel = 0;
    if (/^## /.test(currentLine)) headingLevel = 2;
    else if (/^# /.test(currentLine)) headingLevel = 1;

    button.innerHTML = headingLevel > 0 ? this.translateService.instant('documents.heading', { headingLevel }) : this.translateService.instant('documents.mainText');
  }

  private handleKeydown(event: KeyboardEvent) {
    if (!this.editorInstance) return;

    if (event.code === 'Tab') {
      this.eventPrevent(event);
    }

    if (event.code === 'Space') {
      this.spaceCounter++;

      if (this.spaceCounter >= 3) {
        this.eventPrevent(event);
      }
    } else {
      this.spaceCounter = 0;
    }
  }

  private handlePaste(event: ClipboardEvent) {
    const target = event.target as HTMLElement;
    if (!this.editorInstance || target?.id === 'toastuiLinkUrlInput') return;

    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData?.getData('text') || '';
    const offset = pastedText.length;
    const selection = this.editorInstance.getSelection();
    const newCursorPos = selection[0] + offset;

    setTimeout(() => {
      let markdown = this.editorInstance.getMarkdown();
      markdown = this.cleanMarkdown(markdown);

      this.editorInstance.setMarkdown(markdown, true);

      this.editorInstance.setSelection(newCursorPos);
    }, 0);
  }

  private cleanMarkdown(markdown: string): string {
    const lines = markdown.split('\n');
    let insideCodeBlock = false;

    const cleanLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];

      if (line.trim().startsWith('```')) {
        insideCodeBlock = !insideCodeBlock;
        cleanLines.push(line);
        continue;
      }

      if (insideCodeBlock) {
        cleanLines.push(line);
        continue;
      }

      const trimmedLine = line.trim();

      // Verifica se a linha é vazia e mantem se tiver tabela acima
      if (trimmedLine === '' || trimmedLine === '<br>') {
        const prev = lines[i - 1]?.trim();
        const isPrevList = prev?.match(/^[-*+] /) || prev?.match(/^\d+\.\s/);

        if (isPrevList) {
          cleanLines.push(''); // mantém linha em branco
          continue;
        }

        continue;
      }

      line = trimmedLine;
      line = line.replace(/^>\s?/, ''); // blockquote
      line = line.replace(/`([^`]*)`/g, '$1'); // inline code
      line = line.replace(/^#{3,6}\s*/, ''); // headings H3+
      line = line.replace(/<br>/, ''); // remove <br> tags
      line = line.replace(/!\[.*?]\((http.*?)\)/g, ''); // remove imagem
      line = line === '***' ? '' : line; // remove horizontal line
      cleanLines.push(line);
    }

    return cleanLines.join('\n');
  }

  private handleDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const blob = files[0];

    this.processImageBlob(blob, url => {
      this.editorInstance.exec('addImage', {
        imageUrl: url,
        altText: (blob as File).name || 'image'
      });
    });
  }

  private processImageBlob(blob: Blob, onValid: (url: string) => void): boolean {
    const maxSizeInBytes = 300 * 1000; // 300 KB

    if (!blob.type.startsWith('image/png')) {
      this.notyService.error(this.translateService.instant('notyService.onlyPngImageAllowed'));
      return false;
    }

    if (blob.size > maxSizeInBytes) {
      this.notyService.error(this.translateService.instant('notyService.editorMaxImageSize'));
      return false;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageUrl = reader.result as string;
      onValid(imageUrl);
    };
    reader.readAsDataURL(blob);
    return true;
  }

  private eventPrevent(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
}
